import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';

import * as _ from "underscore";

import { FilterParams } from '../common-interfaces-classes/FilterParams';
import { CallStage } from '../enums/CallStage';
import { GeneralConstants } from '../constants/GeneralConstants';
import { SuggestedTitle } from "../openai/suggested-titles/suggested-titles.component";

@Injectable({
  providedIn: 'root'
})
export class FilterDataHandlerService {

  constructor(
    private route: Router,
    private activatedRoute: ActivatedRoute
  ) {
    this.filterData = new FilterParams();
    this.reset();
  }

  private filterData : FilterParams;
  private continentUpdated: Subject<any> = new Subject();

  get fetchFilterData() : FilterParams {
    return Object.assign({}, this.filterData);
  }

  setFilterData(filterData: FilterParams) {
    this.filterData = Object.assign({}, filterData);
  }

  private filterMethodSubject: Subject< {methodName: string} > = new Subject();

  get filterMethodEvent(): Observable<{methodName: string}> {
    return this.filterMethodSubject.asObservable();
  }

  emitfilterMethodObject(filterMethodObject: {methodName: string}) {
    this.filterMethodSubject.next(filterMethodObject);
  }

  /**
   * @param type Accepts type of aggregate if we need to reset a specific filter type. 'all' is default value
   */
  reset(type: string = "all") {
    type = type.trim().toLowerCase();
    if (type === "all" || type === "jobtitle") {
      this.filterData.jobTitleSelectedList = [];
      this.filterData.jobTitleStatus = CallStage.CURRENT.toString();
    }
    if (type === "all" || type === "location") {
      this.filterData.locationSelectedList = [];
      this.filterData.continentstobeincluded = [];
    }
    if (type === "all" || type === "company") {
      this.filterData.companySelectedList = [];
      this.filterData.companyStatus = CallStage.CURRENT.toString();
    }
    if (type === "all" || type === "industry") {
      this.filterData.industrySelectedList = [];
      this.filterData.industryStatus = CallStage.CURRENT.toString();
    }
    if (type === "all" || type === "education") {
      this.filterData.educationSelectedList = [];
    }
    if (type === "all" || type === "fieldofstudy") {
      this.filterData.fieldOfStudySelectedList = [];
    }
    if (type === "all" || type === "gender") {
      this.filterData.gender = 'All';
      this.filterData.isGenderSelected = false;
      this.filterData.isFemaleMatrixSelected = false;
      this.filterData.isMaleMatrixSelected = false;
    }
    if (type === "all" || type === "school") {
      this.filterData.schoolSelectedList = [];
    }
    if(type == 'all' || type === 'schoollocation'){
      this.filterData.schoolLocationSelectedList = [];
    } 
    if (type === "all" || type === "companysize") {
      this.filterData.companysize = [];
      this.filterData.isCompanySizeSelected = false;
    }
    if (type === "all" || type === "yearsofexperience") {
      this.filterData.filterAppliedOnYearsOfExperience = false;
      this.filterData.yearsofexperience = 'Total';
      this.filterData.yearsofexperienceFrom = null;
      this.filterData.yearsofexperienceTo = null;
    }
    if (type === "all" || type === "skills") {
      this.filterData.skillsSelectedList = [];
    }
    if (type === "all" || type === "likelytomovein") {
      this.filterData.filterLikelyToMoveIn = 'All'
      this.filterData.candidateslikelytomove = 'All';
      this.filterData.isLikelyToMoveInFilterSelected = false;
    }
    if (type === "all" || type === "seniority") {
      this.filterData.seniority = [];
    }
    if (type === "all" || type === "currentyearsofexperience") {
      this.filterData.filterAppliedOnCurrentYearsOfExperience = false;
      this.filterData.currentfromyearsofexperience = null;
      this.filterData.currenttoyearsofexperience = null;
    }
    if (type === "all" || type === "functions") { this.filterData.functionSelectedList = []; }
    if (type === "all" || type === "keyword") { this.filterData.keywordSelectedList = []; }
    if (type === "all" || type === "talentdiscoveryhistoryid") { this.setTalentDiscoveryHistoryID(); }
    if (
      (type === "all" || type === "booleanquery") &&
      !this.isPersonSearchListingPage
    ) { this.setBooleanQuery(); }
    // if (type === "all" || type === "changedjobs") { this.filterData.changedJobStatus = 'All'; }
    // if (type === "all" || type === "booleanquery") { this.setBooleanQuery(); }
    if (type === "all" || type === "changedjobs") {
      this.filterData.changedJobStatus = 'all';
      this.filterData.changedJobStartDuration = 0;
      this.filterData.changedJobEndDuration = 12;
    }
    if (type === "all" || type === GeneralConstants.FILTER_NAMES.PAST_JOB_TITLE) {
      this.filterData.pastJobTitleSelectedList = [];
    }
    if(type == "all" || type == "fullnames") {
      this.filterData.fullnameSelectedList = [];
    }
  }

  setTalentDiscoveryHistoryID(talentdiscoveryhistoryid: string = null) {
    this.filterData.talentdiscoveryhistoryid = talentdiscoveryhistoryid;
  }

  setChangedJobFilter(changedjob: string = null){
    this.filterData.changedJobStatus = changedjob;
  }

  setBooleanQuery(booleanquery: string = null) {
    this.filterData.booleanquery = booleanquery;
  }

  setGender(gender) {
    this.filterData.gender = gender;
    if (gender && ["male", "female"].includes(gender?.toLowerCase())) this.filterData.isGenderSelected = true;
  }

  shouldShowFilter(filterName: string) {
    const url = this.route.url;

    if (url?.startsWith("/report-details"))
      return GeneralConstants.TALENT_INSIGHTS_APPLICABLE_FILTERS.has(filterName);

    if (url?.startsWith("/person/search/listing"))
      return GeneralConstants.PERSON_SEARCH_APPLICABLE_FILTERS.has(filterName);
    
    if (url?.startsWith("/company-details"))
      return GeneralConstants.COMPANY_INSIGHTS_APPLICABLE_FILTERS.has(filterName);

    return false;
  }

  /**
   * For now we are only blocking
   * @returns Whether we should call person search recommended candidate API for current path.
   */
  shouldSendPersonSearchRequest() {
    const validURLs = [
      "/person/search", "/report-details", 
    ];
    const url = this.route.url;
    for (const validURL of validURLs) {
      if (url?.startsWith(validURL))
        return true;
    }
    return false;
  }



  /**
   * As of now we are only using the filter status in person search listing and insights so we will only show it for listing path.
   * @returns boolean value indicating whether or not we should show the option to status of specific filter(current, past, current or past)
   */
  shouldShowStatus() {
    return this.isPersonSearchListingPage();
  }

  isPersonSearchListingPage() {
    const url = this.route.url;
    return url?.startsWith("/person/search/listing");
  }

  get isRedirectFromTalentInsightsPage() {
    const source = this.activatedRoute.snapshot.queryParams['redirectSource'];
    if(source == 'talent-insights')
      return source === GeneralConstants.REDIRECT_SOURCES.TALENT_INSIGHTS;
   
  }

  get isRedirectFromCompanyInsightsPage() {
    const source = this.activatedRoute.snapshot.queryParams['redirectSource'];
    return GeneralConstants.REDIRECT_SOURCES.COMPANY_INSIGHTS === source;
  }

  get isCompanyInsightsPage() {
    const url = this.route.url;
    return url?.startsWith("/company-details");
  }

  get isReportDetailsPage() {
    const url = this.route.url;
    return url?.startsWith("/report-details");
  }

  private personSearchListingInsightsFilter = new Subject<any>();

  setPersonSearchListingInsightsFilter(type: string, event: any) {
    this.personSearchListingInsightsFilter.next({event, type});
  }

  get fetchPersonSearchListingInsightsFilter(): Observable<any> {
    return this.personSearchListingInsightsFilter.asObservable();
  }

  setUpdatedContinent(data) {
    this.continentUpdated.next({ data });
  }

  fetchUpdatedContinent(): Observable<any> {
    return this.continentUpdated.asObservable();
  }

  /**
   * @param type Accepts Average/Total
   */
  setYOEType(type: string) {
    this.filterData.yearsofexperience = type;
  }

  get yoeType() {
    return this.filterData.yearsofexperience;
  }

  private yearsOfExperienceTypeSubject: Subject<{yoeType: string, emmitedFrom: string}> = new Subject();

  get yearsOfExperienceTypeEvent() : Observable<{yoeType: string, emmitedFrom: string}> {
    return this.yearsOfExperienceTypeSubject.asObservable();
  }

  emitYearsOfExperienceType(type: {yoeType: string, emmitedFrom: string}) {
    this.yearsOfExperienceTypeSubject.next(type);
  }

  private aiSuggestionDataSubject: Subject<{ suggestedTitle: SuggestedTitle, [key: string]: any }[]> = new Subject();

  get fetchAISuggestionData(): Observable<{ suggestedTitle: SuggestedTitle, [key: string]: any }[]> {
    return this.aiSuggestionDataSubject.asObservable();
  }

  pushAISuggestionData(data: { suggestedTitle: SuggestedTitle, [key: string]: any }[]) {
    this.aiSuggestionDataSubject.next(data);
  }


  private conomanyName: Subject<any> = new Subject();

  setCompanyFilter(company: any[]) {
    this.filterData.companySelectedList = _.clone(company, true);
    this.conomanyName.next({ company });
  }

  fetchCompanyFilter(): Observable<any> {
    return this.conomanyName.asObservable();
  }

}
